/**
 * Lesson Viewer Component
 * Interactive lesson display with content, learning objectives, and progression
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { megaTrainerService } from '../../services/megaTrainerService';
import { lessonsService } from '../../services/lessonsService';
import Icon from '../../components/AppIcon';
import LoadingState from '../../components/ui/LoadingState';

const LessonViewer = () => {
  const { moduleId, lessonNumber } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lesson, setLesson] = useState(null);
  const [module, setModule] = useState(null);
  const [related, setRelated] = useState([]);
  const [bestScore, setBestScore] = useState(0);
  const [stage, setStage] = useState('start'); // start -> objectives -> lesson -> practice
  const [checkpointResponses, setCheckpointResponses] = useState({});
  const [checkpointResult, setCheckpointResult] = useState(null);
  const [branchChoice, setBranchChoice] = useState(null);
  const [branchFeedback, setBranchFeedback] = useState(null);
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const REQUIRED_SCORE = 90;

  const normalizeCheckpointQuestions = (src, keyPointsFallback = []) => {
    if (Array.isArray(src) && src.length > 0) {
      return src.map((q, idx) => ({
        id: q?.id || `q${idx + 1}`,
        question: q?.question || q?.prompt || 'Quick check',
        correct: q?.correct || 'A',
        options: q?.options || {
          A: 'Yes',
          B: 'No',
          C: 'Maybe'
        }
      }));
    }
    // fallback from keypoints
    return (keyPointsFallback || []).slice(0, 2).map((point, idx) => ({
      id: `q${idx + 1}`,
      question: 'What is the critical action in this lesson?',
      correct: 'A',
      options: {
        A: String(point).replace(/^[-•]\s*/, ''),
        B: 'Skip probing and move on',
        C: 'Respond with a generic pitch'
      }
    }));
  };

  const normalizeBranching = (src) => {
    if (src && typeof src === 'object' && Array.isArray(src.options) && src.options.length > 0) {
      return {
        prompt: src.prompt || 'Choose your move',
        options: src.options.map((o, idx) => ({
          id: o?.id || `opt${idx + 1}`,
          label: o?.label || o?.text || `Option ${idx + 1}`,
          feedback: o?.feedback || 'Good choice.',
        })),
        best: src.best || src.options[0]?.id || 'opt1'
      };
    }
    return {
      prompt: 'Prospect says: "Your price feels high." Pick your move.',
      options: [
        { id: 'probe', label: 'Probe: "When you say high, is that vs budget or ROI?"', feedback: 'Best. You stay curious and get specifics.' },
        { id: 'discount', label: 'Offer a discount immediately', feedback: 'Weak. You lose leverage and avoid the real objection.' },
        { id: 'defend', label: 'Defend value without asking questions', feedback: 'Risky. You talk past their concern.' }
      ],
      best: 'probe'
    };
  };

  useEffect(() => {
    loadLesson();
  }, [moduleId, lessonNumber, user?.id]);

  useEffect(() => {
    const onStorage = () => {
      setBestScore((prev) => Math.max(prev, getBestScoreLocal(moduleId, lessonNumber)));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [moduleId, lessonNumber]);

  const loadLesson = async () => {
    try {
      setLoading(true);

      // Get module
      const mod = megaTrainerService.TRAINING_MODULES[moduleId];
      if (!mod) throw new Error('Module not found');
      setModule(mod);

      // Fetch lesson from lessons table
      const lessonData = await lessonsService.getLesson(moduleId, Number(lessonNumber));
      
      if (!lessonData) {
        // Fallback: try to get from coaching concepts (legacy)
        const pillar = mod.pillars[parseInt(lessonNumber) - 1] || mod.pillars[0];
        const lessonContent = await megaTrainerService.getCoachingLesson(moduleId, pillar);
        
        // Normalize key points (array or newline-delimited string)
        let keyPoints = [];
        const rawPoints = lessonContent?.bullet_points;
        if (Array.isArray(rawPoints)) {
          keyPoints = rawPoints.filter(Boolean).map((p) => String(p));
        } else if (typeof rawPoints === 'string') {
          keyPoints = rawPoints
            .split(/\r?\n|•/)
            .map((s) => s.trim().replace(/^[-•]\s*/, ''))
            .filter(Boolean);
        }

        // Normalize difficulty (number 1-5)
        const rawDifficulty = lessonContent?.difficulty;
        const difficulty = typeof rawDifficulty === 'number'
          ? rawDifficulty
          : ({
              beginner: 1,
              Beginner: 1,
              intermediate: 3,
              Intermediate: 3,
              advanced: 5,
              Advanced: 5
            }[rawDifficulty] || 3);

        setLesson({
          number: parseInt(lessonNumber),
          pillar,
          title: lessonContent?.title || pillar,
          summary: lessonContent?.summary,
          keyPoints,
          example: lessonContent?.example_script,
          difficulty,
          totalLessons: mod.lessons,
          checkpointQuestions: normalizeCheckpointQuestions(null, keyPoints),
          branchingScenario: normalizeBranching(null)
        });
      } else {
        // Build learning objectives from rich JSON structure
        const learningObjectives = Array.isArray(lessonData.learning_objectives)
          ? lessonData.learning_objectives
          : [];
        
        const keyPoints = learningObjectives.map((obj) => 
          typeof obj === 'string' ? obj : obj.description || obj.title || ''
        ).filter(Boolean);

        // Extract trainer breakdown (handle both JSONB and TEXT formats)
        const breakdown = lessonData.trainer_breakdown || {};
        const trainerContent = {
          whyItMatters: breakdown.why_it_matters || lessonData.trainer_breakdown_why_it_matters || '',
          whatGoodLooksLike: breakdown.what_good_looks_like || lessonData.trainer_breakdown_what_good_looks_like || '',
          whatBadLooksLike: breakdown.what_bad_looks_like || lessonData.trainer_breakdown_what_bad_looks_like || '',
          phrasesToUse: breakdown.phrases_to_use || lessonData.trainer_breakdown_phrases_to_use || '',
          mistakesToAvoid: breakdown.mistakes_to_avoid || lessonData.trainer_breakdown_mistakes_to_avoid || '',
          exampleResponse: breakdown.example_response || ''
        };

        const checkpointQuestions = normalizeCheckpointQuestions(lessonData.checkpoint_questions, keyPoints);
        const branchingScenario = normalizeBranching(lessonData.branching_scenario);

        setLesson({
          number: parseInt(lessonNumber),
          pillar: lessonData.lesson_title,
          title: lessonData.lesson_title,
          summary: lessonData.overview,
          keyPoints: keyPoints.length > 0 ? keyPoints : ['Loading lesson content...'],
          example: trainerContent.exampleResponse,
          difficulty: lessonData.difficulty || 2,
          totalLessons: mod.lessons,
          // Rich training content
          overview: lessonData.overview || '',
          scenarioSetup: lessonData.scenario_setup || '',
          trainerContent,
          practiceDrill: lessonData.practice_drill || '',
          scoringRubric: lessonData.scoring_rubric || {},
          reflectionQuestions: lessonData.reflection_questions || [],
          successCriteria: lessonData.success_criteria || '',
          checkpointQuestions,
          branchingScenario,
          fullData: lessonData
        });
      }

      const rel = megaTrainerService.getRelatedLessons(moduleId, lessonData?.lesson_title || mod.pillars[parseInt(lessonNumber) - 1], 4);
      setRelated(rel);

      const localBest = getBestScoreLocal(moduleId, lessonNumber);
      let remoteBest = 0;
      if (user?.id) {
        remoteBest = await megaTrainerService.getLessonPracticeScore(user.id, moduleId, Number(lessonNumber));
      }
      setBestScore(Math.max(localBest, remoteBest));
    } catch (error) {
      console.error('Error loading lesson:', error);
    } finally {
      setLoading(false);
    }
  };
  const getBestScoreLocal = (modId, lessonNum) => {
    const key = `trainer_best_score_${modId}_${lessonNum}`;
    const val = Number(localStorage.getItem(key));
    return Number.isFinite(val) ? val : 0;
  };

  const isUnlocked = bestScore >= REQUIRED_SCORE;

  const checkpointQuestions = lesson?.checkpointQuestions || [];
  const branchingScenario = lesson?.branchingScenario || normalizeBranching(null);
  const checkpointPassed = checkpointResult && checkpointResult.correct === checkpointResult.total;

  const handleCheckpointSubmit = () => {
    const total = checkpointQuestions.length;
    const correct = checkpointQuestions.filter(q => checkpointResponses[q.id] === q.correct).length;
    setCheckpointResult({ correct, total });
  };

  const handleBranchSelect = (id) => {
    setBranchChoice(id);
    const scenario = branchingScenario;
    const selected = scenario.options.find(o => o.id === id);
    if (!selected) return;
    const good = id === scenario.best;
    setBranchFeedback({ good, text: selected.feedback });
  };

  const normalizeRubric = (rubric) => {
    if (!rubric) return [];
    if (Array.isArray(rubric)) {
      return rubric.map((item, idx) => {
        if (typeof item === 'string') {
          return { label: `Criteria ${idx + 1}`, description: item, weight: 25 };
        }
        return {
          label: item?.label || item?.name || `Criteria ${idx + 1}`,
          description: item?.criteria || item?.description || (typeof item === 'object' ? JSON.stringify(item) : String(item)),
          weight: item?.weight || item?.score || 25
        };
      });
    }
    if (typeof rubric === 'object') {
      return Object.entries(rubric).map(([key, val]) => ({
        label: key,
        description: typeof val === 'string' ? val : val?.criteria || val?.description || (typeof val === 'object' ? JSON.stringify(val) : String(val)),
        weight: typeof val === 'object' && val?.weight ? val.weight : 25
      }));
    }
    return [];
  };

  const scoringRubricEntries = normalizeRubric(lesson?.scoringRubric);

  const goToObjectives = () => {
    setStage('objectives');
  };

  const goToLesson = () => {
    setStage('lesson');
  };

  const goToPractice = () => {
    navigate(`/app/trainer/${moduleId}/scenarios`, {
      state: { lessonNumber: Number(lessonNumber) }
    });
  };

  const handleNextLesson = () => {
    if (!isUnlocked) return;
    if (lesson.number < lesson.totalLessons) {
      navigate(`/app/trainer/${moduleId}/lesson/${lesson.number + 1}`);
    } else {
      navigate(`/app/trainer/${moduleId}/complete`);
    }
  };
  if (loading) {
    return <LoadingState message="Loading lesson..." />;
  }

  if (!lesson || !module) {
    return <div className="text-center py-12">Lesson not found</div>;
  }

  const phase = lesson ? megaTrainerService.getPhaseFor(moduleId, lesson.pillar) : null;

  // Start Screen - Initial Entry Point
  if (stage === 'start') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate('/app/mega-trainer')}
            className="text-sm text-muted-foreground hover:text-foreground mb-6 flex items-center gap-2"
          >
            <Icon name="ArrowLeft" size={16} />
            Back to Trainer
          </button>

          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-12">
              <p className="text-sm text-muted-foreground font-medium mb-2">{module.name}</p>
              <h1 className="text-4xl font-bold text-foreground mb-4">{lesson.title}</h1>
              <p className="text-lg text-muted-foreground mb-8">{lesson.summary}</p>
              
              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={16} />
                  <span>10-15 min</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Target" size={16} />
                  <span className={`font-medium px-2 py-0.5 rounded ${
                    lesson.difficulty <= 2 ? 'bg-success/20 text-success' :
                    lesson.difficulty === 3 ? 'bg-warning/20 text-warning' :
                    'bg-error/20 text-error'
                  }`}>
                    {lesson.difficulty <= 2 ? 'Beginner' : lesson.difficulty === 3 ? 'Intermediate' : 'Advanced'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="BookOpen" size={16} />
                  <span>Lesson {lesson.number} of {lesson.totalLessons}</span>
                </div>
              </div>

              <button
                onClick={goToObjectives}
                className="px-8 py-4 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold text-lg flex items-center gap-3 transition-all shadow-lg hover:shadow-xl"
              >
                <Icon name="PlayCircle" size={24} />
                Start Lesson
              </button>
            </div>

            <div className="p-8 border-t border-border">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Lightbulb" size={20} className="text-accent" />
                What You'll Learn
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lesson.keyPoints.slice(0, 4).map((point, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span>{point.substring(0, 60)}{point.length > 60 ? '...' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Objectives Screen - Learning Goals
  if (stage === 'objectives') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => setStage('start')}
            className="text-sm text-muted-foreground hover:text-foreground mb-6 flex items-center gap-2"
          >
            <Icon name="ArrowLeft" size={16} />
            Back
          </button>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-foreground">
                Lesson {lesson.number} of {lesson.totalLessons}
              </h3>
              <span className="text-sm text-muted-foreground">
                {Math.round((lesson.number / lesson.totalLessons) * 100)}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${(lesson.number / lesson.totalLessons) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-card rounded-xl border border-border overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-8">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-primary/20 rounded-lg">
                  <Icon name="Target" className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground">Key Learning Objectives</h2>
                  <p className="text-sm text-muted-foreground mt-1">Master these concepts to advance</p>
                </div>
              </div>
            </div>

            <div className="p-8">
              {lesson.keyPoints.length > 0 ? (
                <ul className="space-y-6 mb-8">
                  {lesson.keyPoints.map((point, idx) => (
                    point.trim() && (
                      <li key={idx} className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-lg font-bold text-primary">{idx + 1}</span>
                        </div>
                        <div>
                          <p className="text-foreground leading-relaxed text-lg">{point.replace(/^[-•]\s*/, '')}</p>
                        </div>
                      </li>
                    )
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground mb-8">Loading objectives...</p>
              )}

              <button
                onClick={goToLesson}
                className="w-full px-6 py-4 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 transition-all"
              >
                <Icon name="BookOpen" size={20} />
                Continue to Lesson
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Lesson Content Screen - Full Material
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 pb-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Breadcrumb */}
        <button
          onClick={() => setStage('objectives')}
          className="text-sm text-muted-foreground hover:text-foreground mb-6 flex items-center gap-2"
        >
          <Icon name="ArrowLeft" size={16} />
          Back to Objectives
        </button>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-foreground">
              Lesson {lesson.number} of {lesson.totalLessons}
            </h3>
            <span className="text-sm text-muted-foreground">
              {Math.round((lesson.number / lesson.totalLessons) * 100)}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(lesson.number / lesson.totalLessons) * 100}%` }}
            />
          </div>
        </div>

        {/* Module Context */}
        <div className="mb-8">
          <p className="text-sm text-muted-foreground font-medium">{module.name}</p>
          <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">{lesson.title}</h1>
          <p className="text-lg text-muted-foreground">{lesson.summary}</p>
        </div>

        {/* Overview Section */}
        {lesson.overview && (
          <div className="bg-card rounded-xl border border-border p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/20 rounded-lg">
                <Icon name="BookOpen" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Overview</h2>
                <p className="text-sm text-muted-foreground">Understanding the fundamentals</p>
              </div>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-foreground leading-relaxed whitespace-pre-wrap">{lesson.overview}</p>
            </div>
          </div>
        )}

        {/* Why It Matters */}
        {lesson.trainerContent?.whyItMatters && (
          <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-xl border border-accent/20 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-accent/20 rounded-lg">
                <Icon name="Target" className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Why This Matters</h2>
                <p className="text-sm text-muted-foreground">The business impact of mastering this skill</p>
              </div>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-foreground leading-relaxed whitespace-pre-wrap">{lesson.trainerContent.whyItMatters}</p>
            </div>
          </div>
        )}

        {/* What Good Looks Like vs Bad */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {lesson.trainerContent?.whatGoodLooksLike && (
            <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-xl border border-success/20 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-success/20 rounded-lg">
                  <Icon name="CheckCircle" className="w-5 h-5 text-success" />
                </div>
                <h3 className="text-lg font-bold text-foreground">What Good Looks Like</h3>
              </div>
              <p className="text-foreground leading-relaxed whitespace-pre-wrap text-sm">{lesson.trainerContent.whatGoodLooksLike}</p>
            </div>
          )}

          {lesson.trainerContent?.whatBadLooksLike && (
            <div className="bg-gradient-to-br from-error/10 to-error/5 rounded-xl border border-error/20 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-error/20 rounded-lg">
                  <Icon name="XCircle" className="w-5 h-5 text-error" />
                </div>
                <h3 className="text-lg font-bold text-foreground">What Bad Looks Like</h3>
              </div>
              <p className="text-foreground leading-relaxed whitespace-pre-wrap text-sm">{lesson.trainerContent.whatBadLooksLike}</p>
            </div>
          )}
        </div>

        {/* Phrases to Use */}
        {lesson.trainerContent?.phrasesToUse && (
          <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl border border-secondary/20 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-secondary/20 rounded-lg">
                <Icon name="MessageSquare" className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Phrases to Use</h2>
                <p className="text-sm text-muted-foreground">Word-for-word scripts you can adapt</p>
              </div>
            </div>
            <div className="space-y-3">
              {(typeof lesson.trainerContent.phrasesToUse === 'string' 
                ? lesson.trainerContent.phrasesToUse.split('\n') 
                : Array.isArray(lesson.trainerContent.phrasesToUse) 
                  ? lesson.trainerContent.phrasesToUse 
                  : []
              ).filter(p => p && String(p).trim()).map((phrase, idx) => (
                <div key={idx} className="bg-card rounded-lg p-4 border border-border">
                  <p className="text-foreground italic">{phrase.replace(/^[-•]\s*/, '').trim()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Branching Scenario */}
        <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl border border-secondary/20 p-8 mb-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-secondary/20 rounded-lg">
              <Icon name="GitBranch" className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Choose Your Move</h2>
              <p className="text-sm text-muted-foreground">Branching practice to apply the concept</p>
            </div>
          </div>
          <p className="text-foreground mb-4 font-medium">{branchingScenario.prompt}</p>
          <div className="space-y-3">
            {branchingScenario.options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleBranchSelect(opt.id)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-all ${
                  branchChoice === opt.id ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {branchFeedback && (
            <div className={`mt-4 p-4 rounded-lg border ${branchFeedback.good ? 'border-success/30 bg-success/10' : 'border-warning/30 bg-warning/10'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon name={branchFeedback.good ? 'CheckCircle' : 'AlertCircle'} className={`w-5 h-5 ${branchFeedback.good ? 'text-success' : 'text-warning'}`} />
                <span className={`font-semibold ${branchFeedback.good ? 'text-success' : 'text-warning'}`}>
                  {branchFeedback.good ? 'Great choice' : 'Better option available'}
                </span>
              </div>
              <p className="text-sm text-foreground">{branchFeedback.text}</p>
            </div>
          )}
        </div>

        {/* Mistakes to Avoid */}
        {lesson.trainerContent?.mistakesToAvoid && (
          <div className="bg-gradient-to-br from-warning/10 to-warning/5 rounded-xl border border-warning/20 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-warning/20 rounded-lg">
                <Icon name="AlertTriangle" className="w-6 h-6 text-warning" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Mistakes to Avoid</h2>
                <p className="text-sm text-muted-foreground">Common pitfalls that kill deals</p>
              </div>
            </div>
            <div className="space-y-3">
              {(typeof lesson.trainerContent.mistakesToAvoid === 'string' 
                ? lesson.trainerContent.mistakesToAvoid.split('\n') 
                : Array.isArray(lesson.trainerContent.mistakesToAvoid) 
                  ? lesson.trainerContent.mistakesToAvoid 
                  : []
              ).filter(m => m && String(m).trim()).map((mistake, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Icon name="X" size={18} className="text-warning flex-shrink-0 mt-1" />
                  <p className="text-foreground">{mistake.replace(/^[-•]\s*/, '').trim()}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Example Section */}
        {lesson.example && (
          <div className="bg-card rounded-xl border border-border p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/20 rounded-lg">
                <Icon name="MessageCircle" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Example Response</h2>
                <p className="text-sm text-muted-foreground">See it in action</p>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-6 border border-border">
              <p className="text-foreground italic text-lg whitespace-pre-wrap">"{lesson.example}"</p>
            </div>
          </div>
        )}

        {/* Scenario Setup */}
        {lesson.scenarioSetup && (
          <div className="bg-card rounded-xl border border-border p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/20 rounded-lg">
                <Icon name="Users" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Scenario Context</h2>
                <p className="text-sm text-muted-foreground">When you'll use this</p>
              </div>
            </div>
            <p className="text-foreground leading-relaxed whitespace-pre-wrap">{lesson.scenarioSetup}</p>
          </div>
        )}

        {/* Checkpoint Quiz (before practice) */}
        {checkpointQuestions.length > 0 && (
          <div className="bg-card rounded-xl border border-border p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-accent/20 rounded-lg">
                <Icon name="HelpCircle" className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Checkpoint</h2>
                <p className="text-sm text-muted-foreground">Pass this quick check to unlock practice</p>
              </div>
            </div>
            {!showCheckpoint ? (
              <div className="flex items-center justify-between flex-col sm:flex-row gap-3">
                <p className="text-muted-foreground">Ready for a quick 2-question check?</p>
                <button
                  onClick={() => setShowCheckpoint(true)}
                  className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium"
                >
                  Start Checkpoint
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {checkpointQuestions.map((q) => (
                  <div key={q.id} className="space-y-3">
                    <p className="font-semibold text-foreground">{q.question}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {Object.entries(q.options).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setCheckpointResponses((prev) => ({ ...prev, [q.id]: key }))}
                          className={`px-4 py-3 rounded-lg border text-left transition-all ${
                            checkpointResponses[q.id] === key
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border bg-muted hover:bg-muted/70'
                          }`}
                        >
                          <span className="font-semibold mr-2">{key}.</span>
                          <span className="text-foreground">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleCheckpointSubmit}
                    className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium"
                  >
                    Check Answers
                  </button>
                  {checkpointResult && (
                    <span className={`text-sm font-semibold ${checkpointPassed ? 'text-success' : 'text-warning'}`}>
                      {checkpointResult.correct}/{checkpointResult.total} correct
                      {!checkpointPassed && ' — review the lesson and retry'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Practice Drill Instructions */}
        {lesson.practiceDrill && (
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl border border-primary/20 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-primary/20 rounded-lg">
                <Icon name="Zap" className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Practice Drill</h2>
                <p className="text-sm text-muted-foreground">Your assignment</p>
              </div>
            </div>
            <p className="text-foreground leading-relaxed whitespace-pre-wrap mb-6">{lesson.practiceDrill}</p>
            <button
              onClick={goToPractice}
              disabled={checkpointQuestions.length > 0 && !checkpointPassed}
              className={`w-full px-6 py-4 bg-primary hover:bg-primary/90 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 transition-all shadow-lg hover:shadow-xl ${
                checkpointQuestions.length > 0 && !checkpointPassed ? 'opacity-60 cursor-not-allowed hover:bg-primary' : ''
              }`}
            >
              <Icon name="PlayCircle" size={24} />
              Start Practice Scenario
            </button>
            {checkpointQuestions.length > 0 && !checkpointPassed && (
              <p className="text-sm text-warning mt-3">Pass the checkpoint to unlock practice.</p>
            )}
          </div>
        )}

        {/* Scoring Rubric */}
        {scoringRubricEntries.length > 0 && (
          <div className="bg-card rounded-xl border border-border p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-accent/20 rounded-lg">
                <Icon name="Award" className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">How You'll Be Scored</h2>
                <p className="text-sm text-muted-foreground">Evaluation criteria</p>
              </div>
            </div>
            <div className="space-y-4">
              {scoringRubricEntries.map((item, idx) => (
                <div key={`${item.label}-${idx}`} className="border-l-4 border-accent pl-4">
                  <h4 className="font-semibold text-foreground mb-1 capitalize">{item.label} ({item.weight || 25}%)</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Reflection Questions */}
        {lesson.reflectionQuestions && lesson.reflectionQuestions.length > 0 && (
          <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-xl border border-secondary/20 p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-secondary/20 rounded-lg">
                <Icon name="HelpCircle" className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Reflection Questions</h2>
                <p className="text-sm text-muted-foreground">Think about your approach</p>
              </div>
            </div>
            <div className="space-y-4">
              {lesson.reflectionQuestions.map((question, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sm font-bold text-secondary">{idx + 1}</span>
                  </div>
                  <p className="text-foreground">{question}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress to Unlock */}
        <div className="bg-card rounded-xl border border-border p-6 mb-8">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="ShieldCheck" size={16} />
              <span>Required score to advance: {REQUIRED_SCORE}+</span>
            </div>
            <span className={`text-sm font-semibold ${isUnlocked ? 'text-success' : 'text-warning'}`}>
              Best: {Math.round(bestScore)}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${isUnlocked ? 'bg-success' : 'bg-warning'}`}
              style={{ width: `${Math.min(100, (bestScore / REQUIRED_SCORE) * 100)}%` }}
            />
          </div>
          {isUnlocked && (
            <div className="mt-4 pt-4 border-t border-border">
              <button
                onClick={handleNextLesson}
                className="w-full px-6 py-3 bg-success hover:bg-success/90 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <Icon name="CheckCircle" size={18} />
                Lesson Complete - Continue to Next
              </button>
            </div>
          )}
        </div>

        {/* Related Lessons */}
        {related?.length > 0 && (
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Layers" size={18} className="text-muted-foreground" />
              <h3 className="text-lg font-semibold text-foreground">Related Lessons</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((r, idx) => (
                <button
                  key={`${r.moduleId}-${r.lessonNumber}-${idx}`}
                  onClick={() => navigate(r.path)}
                  className="px-4 py-3 bg-muted hover:bg-muted/80 text-foreground rounded-lg border border-border transition-all flex items-center justify-between"
                >
                  <span className="text-left">
                    <span className="block text-sm font-medium">{r.moduleName}</span>
                    <span className="block text-xs text-muted-foreground">{(r.pillar || '').replace(/_/g, ' ')}</span>
                  </span>
                  <Icon name="ArrowRight" size={16} />
                </button>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default LessonViewer;
